import { ChangeEventHandler, useCallback, useState } from 'react'
import tw from 'tailwind-styled-components'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import { useForm } from 'react-hook-form'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import {
  Card,
  TextInput,
  Button,
  NavBar,
  FooterLanding,
  Form,
  Modal,
  BackgroundImage,
  CheckBox,
  ErrorMessages,
} from '@foros-fe/core'
import {
  StartAuctionInquiryInput,
  StartAuctionIntent,
  useCreateStartAuctionInquiryMutation,
} from '@foros-fe/graphql'
import Background from '@foros-fe/assets/images/filtered-forest.png'
import { getErrorTranslations } from '@foros-fe/core/utils'

const Container = tw.div`container py-16 flex flex-col items-center`
const Title = tw.p`
  text-white
  text-3xl
  font-bold
`
const CenteredCard = tw(Card)`shadow flex-1 space-y-4 self-stretch md:self-center min-w-[50%]`

type FormData = StartAuctionInquiryInput

export default function AuctionInquiry() {
  const [modalOpen, setModalOpen] = useState(false)
  const [createStartAuctionInquiryMutation] = useCreateStartAuctionInquiryMutation()
  const { t } = useTranslation(['inquiries', 'common', 'enum'])
  const form = useForm<FormData>()
  const { register, setError, setValue, watch, formState, clearErrors } = form
  const onSubmit = useCallback(
    async (input: FormData) => {
      if (!input.intent) {
        return setError('intent', { message: t('auction.mustChoose') })
      }

      const { data } = await createStartAuctionInquiryMutation({
        variables: { input },
      })

      const error = data?.createStartAuctionInquiry?.error
      if (error) {
        if (error.email) {
          setError('email', getErrorTranslations('StartAuctionInquiryEmailError', error.email))
        }
        if (error.phoneNumber) {
          setError(
            'phoneNumber',
            getErrorTranslations('StartAuctionInquiryPhoneNumberError', error.phoneNumber)
          )
        }
      } else {
        setModalOpen(true)
      }
    },
    [createStartAuctionInquiryMutation, setError, t]
  )
  const closeModal = useCallback(() => setModalOpen(false), [])
  const setIntent = useCallback<ChangeEventHandler<HTMLInputElement>>(
    ({ target }) => {
      clearErrors('intent')
      setValue('intent', target.value as StartAuctionInquiryInput['intent'])
    },
    [clearErrors, setValue]
  )

  const intent = watch('intent')

  return (
    <div>
      <main>
        <NavBar />
        <div className='relative'>
          <Form {...form} onSubmit={onSubmit}>
            <div className='z-[-1] absolute left-0 right-0 bottom-0 top-0'>
              <BackgroundImage src={Background} priority />
            </div>
            <Container>
              <Title className='mb-4 md:mb-8'>{t('auction.title')}</Title>
              <CenteredCard>
                <div className='flex flex-col space-y-2'>
                  <div className='flex justify-around'>
                    <CheckBox
                      label={t(`${'enum:AuctionSalePurpose.forest_with_land'}`)}
                      name='intentWithLand'
                      value={StartAuctionIntent.WithLand}
                      onChange={setIntent}
                      checked={intent === StartAuctionIntent.WithLand}
                    />
                    <CheckBox
                      label={t(`${'enum:AuctionSalePurpose.forest_for_harvesting'}`)}
                      name='intentForHarvesting'
                      value={StartAuctionIntent.ForCutting}
                      onChange={setIntent}
                      checked={intent === StartAuctionIntent.ForCutting}
                    />
                  </div>
                  <div className='flex justify-center'>
                    <ErrorMessages error={formState.errors.intent} />
                  </div>
                </div>
                <TextInput label={t('common:email')} {...register('email', { required: true })} />
                <TextInput
                  label={t('common:phoneNumber')}
                  {...register('phoneNumber', { required: true })}
                />
                <TextInput
                  label={t('common:enterCadastralNumber')}
                  {...register('cadastralNumber')}
                />
                <Button title={t('common:continue')} value='auction-inquiry-submit' />
              </CenteredCard>
            </Container>
          </Form>
        </div>
        <FooterLanding />
        <Modal open={modalOpen} onClose={closeModal}>
          <p className='text-3xl mb-8'>{t('auction.received')}</p>
          <Button
            title={t('common:continue')}
            value='auction-inquiry-received'
            onClick={closeModal}
          />
        </Modal>
      </main>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ssr: true,
      ...(await serverSideTranslations(locale!, ['common', 'inquiries', 'enum'])),
    },
  }
}
